import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, CheckCircle, Lock, Mail, User, Briefcase } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ActivateAccount({ setUser }) {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [invitationData, setInvitationData] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API}/invitations/verify/${token}`);
      setInvitationData(response.data);
      setLoading(false);
    } catch (error) {
      setError(error.response?.data?.detail || 'Invalid or expired invitation link');
      setLoading(false);
    }
  };

  const handleActivate = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setActivating(true);
    try {
      const response = await axios.post(`${API}/invitations/activate/${token}`, null, {
        params: { password }
      });

      // Store token and user
      localStorage.setItem('hrms_token', response.data.token);
      localStorage.setItem('hrms_user', JSON.stringify(response.data.user));
      setUser(response.data.user);

      toast.success('Account activated successfully! Welcome to My HR Orbit!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate account');
    } finally {
      setActivating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8 text-center">
            <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">⚠️</span>
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Invalid Invitation</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <Button onClick={() => navigate('/login')} className="w-full">
              Go to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <Building2 className="h-10 w-10 text-emerald-500" />
            <h1 className="text-3xl font-bold text-slate-900">My HR Orbit</h1>
          </div>
          <h2 className="text-2xl font-bold text-slate-900">Activate Your Account</h2>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          {/* Welcome Message */}
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-6 mb-8">
            <div className="flex items-start space-x-4">
              <div className="h-12 w-12 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                <CheckCircle className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-emerald-900 mb-2">
                  Welcome, {invitationData?.full_name}!
                </h3>
                <p className="text-emerald-800">
                  You've been invited to join My HR Orbit. Complete your account activation to get started.
                </p>
              </div>
            </div>
          </div>

          {/* Employee Details */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="flex items-center space-x-3 p-4 bg-slate-50 rounded-lg">
              <Mail className="h-5 w-5 text-slate-600" />
              <div>
                <p className="text-xs text-slate-500">Email</p>
                <p className="text-sm font-medium text-slate-900">{invitationData?.email}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-4 bg-slate-50 rounded-lg">
              <User className="h-5 w-5 text-slate-600" />
              <div>
                <p className="text-xs text-slate-500">Employee ID</p>
                <p className="text-sm font-medium text-slate-900">{invitationData?.employee_id}</p>
              </div>
            </div>
            {invitationData?.position && (
              <div className="flex items-center space-x-3 p-4 bg-slate-50 rounded-lg">
                <Briefcase className="h-5 w-5 text-slate-600" />
                <div>
                  <p className="text-xs text-slate-500">Position</p>
                  <p className="text-sm font-medium text-slate-900">{invitationData.position}</p>
                </div>
              </div>
            )}
            {invitationData?.department && (
              <div className="flex items-center space-x-3 p-4 bg-slate-50 rounded-lg">
                <Building2 className="h-5 w-5 text-slate-600" />
                <div>
                  <p className="text-xs text-slate-500">Department</p>
                  <p className="text-sm font-medium text-slate-900">{invitationData.department}</p>
                </div>
              </div>
            )}
          </div>

          {/* Password Form */}
          <form onSubmit={handleActivate} className="space-y-6">
            <div>
              <Label htmlFor="password" className="text-slate-700 font-medium">Create Password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  required
                  data-testid="password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 pl-10 rounded-lg border-slate-300 focus:border-emerald-500 focus:ring-emerald-500/20"
                  placeholder="••••••••"
                  minLength={6}
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">Password must be at least 6 characters</p>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-slate-700 font-medium">Confirm Password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  id="confirmPassword"
                  type="password"
                  required
                  data-testid="confirm-password-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="h-11 pl-10 rounded-lg border-slate-300 focus:border-emerald-500 focus:ring-emerald-500/20"
                  placeholder="••••••••"
                  minLength={6}
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={activating}
              data-testid="activate-btn"
              className="w-full h-11 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold shadow-lg shadow-emerald-500/25"
            >
              {activating ? (
                <div className="flex items-center space-x-2">
                  <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Activating...</span>
                </div>
              ) : (
                'Activate Account'
              )}
            </Button>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-slate-600">
            Already activated your account?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-emerald-600 hover:text-emerald-700 font-medium"
            >
              Log In
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}